// src/theme.js
import { createTheme, Theme } from "@mui/material/styles";
import "@fontsource/noto-sans";

declare module "@mui/material/Paper" {
  interface PaperPropsVariantOverrides {
    paper: true;
  }
}

declare module "@mui/material/Button" {
  interface ButtonPropsVariantOverrides {
    footerLink: true;
  }
}

declare module "@mui/material/styles" {
  interface TypographyVariants {
    dialogtitle: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    dialogtitle?: React.CSSProperties;
  }
}

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    dialogtitle: true;
  }
}

declare module "@mui/material/styles" {
  interface TypographyVariants {
    dialoglabel: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    dialoglabel?: React.CSSProperties;
  }
}

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    dialoglabel: true;
  }
}

declare module "@mui/material/styles" {
  interface TypographyVariants {
    headermenu: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    headermenu?: React.CSSProperties;
  }
}

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    headermenu: true;
  }
}

declare module "@mui/material/styles" {
  interface TypographyVariants {
    toolbarlabel: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    toolbarlabel?: React.CSSProperties;
  }
}

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    toolbarlabel: true;
  }
}

declare module "@mui/material/styles" {
  interface TypographyVariants {
    topLevelBlade: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    topLevelBlade?: React.CSSProperties;
  }
}

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    topLevelBlade: true;
  }
}

declare module "@mui/material/styles" {
  interface TypographyVariants {
    innerLevelBlade: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    innerLevelBlade?: React.CSSProperties;
  }
}

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    innerLevelBlade: true;
  }
}

declare module "@mui/material/styles" {
  interface Theme {
    accent: {
      light: string;
      medium: string;
      hover: string;
    };
    secret: {
      gray: string;
      darkgray: string;
    };
  }
  // allow configuration using `createTheme`
  interface ThemeOptions {
    accent?: {
      light?: string;
      medium?: string;
      hover?: string;
    };
    secret?: {
      gray?: string;
      darkgray?: string;
    };
  }
}

const primary_dark = "#00573D";
const primary_light = "#00805A";
const accent_medium = "#C4CED1";

const theme = createTheme({
  palette: {
    primary: {
      main: primary_dark,
      light: primary_light,
      dark: "#003325",
      contrastText: "#FFF",
    },
    secondary: {
      main: "#004B8D",
      light: "#1DB1DE",
      dark: "#1B2552",
      contrastText: "#FFF",
    },
    error: {
      main: "#B81D47",
      light: "#E0436D",
      dark: "#941C3C",
      contrastText: "#FFF",
    },
    warning: {
      main: "#FA7857",
      light: "#FC9E86",
      dark: "#E35B39",
      contrastText: "#FFF",
    },
    info: {
      main: "#FAE34B",
      light: "#FFEE80",
      dark: "#F5D400",
      contrastText: "#000",
    },
    grey: {
      50: "#F3F3F3",
      100: "#DEDFE0",
      A100: "#C9CACC",
      A200: "#B4B6B8",
      200: "#9FA1A4",
      300: "#8A8C90",
      400: "#75787C",
      500: "#626467",
      600: "#4E4F52",
      700: "#3A3B3D",
      800: "262728",
      900: "#121313",
    },
    action: {
      hover: "#00805A",
    },
  },

  secret: {
    gray: "#F0F0F0",
    darkgray: "#212121",
  },

  accent: {
    light: "#DEE6E8", // Blue Grey
    medium: accent_medium, // Cool Grey
    hover: "#ADBBBF", // Cool Grey Hover
  },

  typography: {
    fontFamily: "Noto Sans, sans-serif",
  },

  components: {
    MuiTypography: {
      variants: [
        {
          props: { variant: "dialogtitle" },
          style: {
            fontSize: "1.25rem",
            textTransform: "uppercase",
          },
        },
        {
          props: { variant: "dialoglabel" },
          style: {
            fontSize: "0.75rem",
            fontWeight: "600",
            textTransform: "uppercase",
            fontFamily: "Noto Sans, sans-serif",
          },
        },
        {
          props: { variant: "headermenu" },
          style: {
            fontSize: "12px",
            fontWeight: "600",
            letterSpacing: ".08em",
          },
        },
        {
          props: { variant: 'toolbarlabel' },
          style: {
            currentColor: (theme:Theme) => theme.palette.common.white,
            fontSize: "8px",
            mb: "2px",
            textAlign: "center",
          },
        },
        {
          props: { variant: "topLevelBlade" },
          style: {
            currentColor: (theme: Theme) => theme.palette.common.black,
            fontSize: "12px",
            fontWeight: "bold",
            padding: "8px",
            fontFamily: "Noto Sans, sans-serif",
          },
        },
        {
          props: { variant: "innerLevelBlade" },
          style: {
            currentColor: (theme: Theme) => theme.palette.common.black,
            fontSize: "12px",
            lineHeight: 1.2,
            padding: "4px",
            fontFamily: "Noto Sans, sans-serif",
          },
        },
      ],
    },
    MuiPaper: {
      variants: [
        {
          props: { variant: "paper" },
          style: {
            padding: "5",
            margin: "0, 0, 5",
            currentcolor: (theme: Theme) => theme.palette.text.primary,
          },
        },
      ],
      styleOverrides: {
        root: {
          boxShadow: "none",
        },
      },
    },

    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          fontSize: "0.75rem",
          fontWeight: "600",
        },
        containedPrimary: {
          padding: "12px 16px",
          width: "280px",
          backgroundColor: primary_dark,
          "&:hover": {
            backgroundColor: primary_light,
          },
        },
        outlinedPrimary: {
          padding: "12px 16px",
          width: "280px",
          color: "black",
          borderColor: "black",
          "&:hover": {
            backgroundColor: accent_medium,
          },
        },
      },
      variants: [
        {
          props: { variant: "footerLink" },
          style: {
            whiteSpace: "nowrap",
            textTransform: "none",
            fontWeight: "normal",
            // backgroundColor: "transparent",
            // color: "white",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.2)", // Adjust the hover background color
            },
          },
        },
      ],
    },

    MuiTextField: {
      defaultProps: {
        InputProps: {
          disableUnderline: true,
          sx: {
            fontSize: "1rem",
            fontWeight: "500",
          },
        },
        InputLabelProps: {
          shrink: false,
          sx: {
            textTransform: "uppercase",
            color: "black",
            fontSize: "0.75rem",
            fontWeight: "600",
          },
        },
      },
    },

    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 0,
        },
      },
    },

    MuiTablePagination: {
      styleOverrides: {
        selectLabel: {
          marginBottom: 0,
        },
        displayedRows: {
          marginBottom: 0,
        },
        actions: {
          marginBottom: 0,
        },
        input: {
          marginRight: "16px",
        },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        head: {
          borderBottom: "1px solid #9ea3a6",
        },
      },
    },

    MuiPaginationItem: {
      styleOverrides: {
        root: {
          "&.MuiPaginationItem-root": {
            color: "#00573D",
          },
          "&.Mui-selected": {
            color: "#121313",
            backgroundColor: "inherit",
          },
          "&.MuiPaginationItem-root:hover": {
            backgroundColor: "inherit",
            color: "#00573D",
          },
          "&.Mui-disabled": {
            opacity: 0,
          },
        },
        text: {
          fontSize: "1rem",
          fontWeight: 400,
          minWidth: "0px",
          padding: "0px",
          marginLeft: "0.3125rem",
          marginRight: "0.3125rem",
        },
        icon: {
          marginRight: "0.3125rem",
          marginLeft: "0.3125rem",
        }
      },
    },

    // MuiLink: {
    //   styleOverrides: {
    //     root: {
    //       color: "white",
    //       backgroundColor: "black",
    //       whiteSpace: "nowrap"
    //     }
    //   }
    // }
  },
});

export default theme;
